// frontend/src/components/landing/ProfezacLanding.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useLandingTranslation } from '../../hooks/useLandingTranslation';
import { countryMappings, getBasicGentilicio } from '../../utils/countryDetection';
import BasicLevelQuiz from './BasicLevelQuiz';
import EmailGuideModal from '../EmailGuideModal';
import SpecialPricingDisplay from '../common/SpecialPricingDisplay';
import QuizVerificationSection from '../common/QuizVerificationSection';
import DailyMotivationSection from '../common/DailyMotivationSection';
import './BasicLevelLanding.css';

const ProfezacLanding = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useLandingTranslation('profezac');
  const [showEmailGuideModal, setShowEmailGuideModal] = useState(false);
  
  // Dynamic pricing states
  const [pricingData, setPricingData] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [loadingPricing, setLoadingPricing] = useState(true);
  
  // Video state management
  const videoRef = useRef(null);


  // Get country-specific text from the same location data used for pricing
  const getCountryText = (countryCode, language = 'es') => {
    const country = countryMappings[countryCode];
    
    if (!country) {
      // Default fallback text based on language
      const defaultText = {
        'es': 'la comunidad latina',
        'en': 'the Latino community', 
        'pt': 'a comunidade latina'
      };
      return {
        name: defaultText[language] || defaultText['es'],
        emoji: '❤️'
      };
    }

    // Get country name in the specified language
    let name;
    switch (language) {
      case 'en':
        name = country.nameEn;
        break;
      case 'pt':
        name = country.namePt;
        break;
      case 'es':
      default:
        name = country.nameEs;
        break;
    }

    return {
      name,
      emoji: country.emoji
    };
  };

  // Get country demonym (like "mexicanos 🇲🇽", "argentinos 🇦🇷", "colombianos 🇨🇴")
  const getCountryDemonym = (countryCode, language = 'es') => {
    const demonyms = {
      'MX': 'mexicanos 🇲🇽',
      'AR': 'argentinos 🇦🇷', 
      'CO': 'colombianos 🇨🇴',
      'PE': 'peruanos 🇵🇪',
      'CL': 'chilenos 🇨🇱',
      'EC': 'ecuatorianos 🇪🇨',
      'VE': 'venezolanos 🇻🇪',
      'BO': 'bolivianos 🇧🇴',
      'PY': 'paraguayos 🇵🇾',
      'UY': 'uruguayos 🇺🇾',
      'CR': 'costarricenses 🇨🇷',
      'PA': 'panameños 🇵🇦',
      'GT': 'guatemaltecos 🇬🇹',
      'HN': 'hondureños 🇭🇳',
      'SV': 'salvadoreños 🇸🇻',
      'NI': 'nicaragüenses 🇳🇮',
      'DO': 'dominicanos 🇩🇴',
      'PR': 'puertorriqueños 🇵🇷',
      'CU': 'cubanos 🇨🇺',
      'ES': 'españoles 🇪🇸',
      'US': 'latinos en 🇺🇸',
      'BR': 'brasileños 🇧🇷'
    };
    
    return demonyms[countryCode] || 'latinos en 🇺🇸';
  };

  // Get currency symbol based on selected currency
  const getCurrencySymbol = () => {
    const currencySymbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'C$',
      'AUD': 'A$',
      'MXN': '$',
      'COP': '$',
      'PEN': 'S/',
      'CLP': '$',
      'ARS': '$',
      'BRL': 'R$'
    };
    return currencySymbols[selectedCurrency] || '$';
  };

  // Fetch pricing data based on user's location
  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        setLoadingPricing(true);
        
        const response = await axios.get('https://portal.profezac.com/api/payments/pricing');
        
        if (response.data.success) {
          setPricingData(response.data.pricing);
          setUserLocation(response.data.location);
          setSelectedCurrency(response.data.location.currency);
          console.log('Pricing data loaded for profezac landing:', response.data);
        } else {
          throw new Error('Failed to fetch pricing data');
        }
      } catch (err) {
        console.error('Error fetching pricing data:', err);
        
        // Set fallback data
        setSelectedCurrency('USD');
      } finally {
        setLoadingPricing(false);
      }
    };

    fetchPricingData();
  }, []);


  // Helper function to get formatted price
  const getFormattedPrice = (level, duration, fallbackPrice = '$5') => {
    if (pricingData && pricingData[level] && pricingData[level][duration]) {
      return pricingData[level][duration].formatted;
    }
    return fallbackPrice;
  };

  // Helper function to get original (crossed-out) price with multiplier
  const getOriginalPrice = (level, duration, multiplier, fallbackPrice = '$15') => {
    const currentPrice = getFormattedPrice(level, duration, fallbackPrice);
    // Extract numeric value from price like "AR$6.655,40", "$5.00", "€4.50", etc.
    const priceMatch = currentPrice.match(/([\d.,]+)/);
    if (priceMatch) {
      // Handle different decimal separators (comma vs period)
      let numericValue = priceMatch[1].replace(',', '.');
      numericValue = parseFloat(numericValue);
      const multipliedValue = numericValue * multiplier;
      
      // Format back to the original format
      let formattedValue;
      if (currentPrice.includes(',')) {
        // Use comma as decimal separator (European style)
        formattedValue = multipliedValue.toFixed(2).replace('.', ',');
      } else {
        // Use period as decimal separator
        formattedValue = multipliedValue.toFixed(2);
      }
      
      // Replace the numeric part while keeping the currency symbol and format
      return currentPrice.replace(/([\d.,]+)/, formattedValue);
    }
    return fallbackPrice;
  };

  return (
    <div className="landing-page">
      {/* Hero Section with Overlaid Elements */}
      <section 
        className="position-relative py-4"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#f8f9fa'
        }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center">
              {/* Hero Image Container - Full Width */}
              <div className="position-relative mb-4">
                <div 
                  className="shadow-lg mx-auto"
                  style={{ 
                    width: '100%',
                    maxWidth: '500px',
                    aspectRatio: '1',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    backgroundImage: 'url("/images/logo-profezac.webp")',
                    backgroundSize: 'cover',
                    backgroundPosition: 'top center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  {/* Yellow tagline overlay on image - HIDDEN */}
                  {/* <div 
                    className="position-absolute bg-warning text-dark fw-bold px-3 py-2 rounded d-inline-block" 
                    style={{ 
                      fontSize: '0.75rem', 
                      top: '20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      maxWidth: 'calc(100% - 40px)',
                      zIndex: 2
                    }}
                  >
                    {t('final_cta.tagline')}
                  </div> */}
                </div>
              </div>

              {/* Features with Checkmarks - Below image for better mobile experience */}
              <div className="row g-2 g-md-3" style={{ maxWidth: '900px', margin: '0 auto' }}>
                <div className="col-sm-6 col-lg-4">
                  <div 
                    className="p-2 p-md-3 rounded text-start"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#333',
                      fontWeight: '500',
                      fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
{t('hero.features.easy_videos')}
                  </div>
                </div>
                <div className="col-sm-6 col-lg-4">
                  <div 
                    className="p-2 p-md-3 rounded text-start"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#333',
                      fontWeight: '500',
                      fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
{t('hero.features.subtitles')}
                  </div>
                </div>
                <div className="col-sm-6 col-lg-4">
                  <div 
                    className="p-2 p-md-3 rounded text-start"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#333',
                      fontWeight: '500',
                      fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
{t('hero.features.progress_tracking')}
                  </div>
                </div>
                <div className="col-sm-6 col-lg-4">
                  <div 
                    className="p-2 p-md-3 rounded text-start"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#333',
                      fontWeight: '500',
                      fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
{t('hero.features.real_time_quizzes')}
                  </div>
                </div>
                <div className="col-sm-6 col-lg-4">
                  <div 
                    className="p-2 p-md-3 rounded text-start"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#333',
                      fontWeight: '500',
                      fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
{t('hero.features.key_words')}
                  </div>
                </div>
                <div className="col-sm-6 col-lg-4">
                  <div 
                    className="p-2 p-md-3 rounded text-start"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#333',
                      fontWeight: '500',
                      fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
{t('hero.features.daily_videos')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Video Section */}
      <section 
        className="py-4 text-white"
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="text-center mb-3">
                <h2 className="display-5 fw-bold mb-3" style={{ textTransform: 'uppercase', letterSpacing: '1px' }}>
                  {t('video_section.header')}
                </h2>
              </div>
              
              <div className="text-center">
                <video
                  ref={videoRef}
                  preload="metadata"
                  poster="/videos/mainvideostilframe.png"
                  className="rounded shadow-lg"
                  onClick={(e) => {
                    if (e.target.paused) {
                      e.target.play();
                    } else {
                      e.target.pause();
                    }
                  }}
                  style={{ 
                    borderRadius: '20px',
                    width: '100%',
                    maxWidth: '600px',
                    height: 'auto',
                    display: 'block',
                    margin: '0 auto',
                    cursor: 'pointer'
                  }}
                  playsInline
                  webkit-playsinline="true"
                  controlsList="noremoteplayback nofullscreen nodownload"
                  disablePictureInPicture
                  x-webkit-airplay="deny"
                >
                  <source src="/videos/0723(1).mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* CTA Button - Right after video */}
                <div className="mt-3">
                  <a
                    href="https://portal.profezac.com/subscription/all5"
                    className="btn btn-warning btn-lg px-5 py-3 fw-bold"
                    style={{
                      fontSize: '1.2rem',
                      borderRadius: '8px',
                      boxShadow: '0 4px 15px rgba(255, 193, 7, 0.4)',
                      textDecoration: 'none',
                      display: 'inline-block',
                      minWidth: '280px'
                    }}
                  >
                    🚀 ¡Empezar Ahora!
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zac Pointing Section */}
      <section className="py-4" style={{ backgroundColor: 'white' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="text-center">
                <img 
                  src="/images/zacpointingdown.png" 
                  alt="Profe Zac pointing down" 
                  className="img-fluid"
                  style={{ maxWidth: '300px', height: 'auto' }}
                />
              </div>
              <div 
                className="bg-white rounded shadow-sm p-4 mt-n2 position-relative"
                style={{ zIndex: 1 }}
              >
                <p className="text-center mb-0 fs-6 text-dark fw-medium" style={{ lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: `"${t('motivational.title').replace('{COUNTRY_DEMONYM}', userLocation?.countryCode ? getCountryDemonym(userLocation.countryCode) : 'latinos en 🇺🇸')}"` }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Levels Section */}
      <section className="py-4" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="text-center mb-3">
            <h2 className="display-5 fw-bold mb-3" style={{ color: '#333' }}>
              {t('learning_system.title')}
            </h2>
          </div>
          
          <div className="row g-4">
            {/* Basic Level */}
            <div className="col-lg-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3">
                    <img 
                      src="/images/basic.png" 
                      alt="Basic Level" 
                      className="img-fluid"
                      style={{ maxHeight: '150px' }}
                    />
                  </div>
                  <div className="plan-title text-center">
                    <h4 className="card-title text-primary fw-bold">{t('level_cards.basic.title')}</h4>
                  </div>
                  <div className="plan-description">
                    <p className="card-text text-muted">
                      {t('level_cards.basic.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Level */}
            <div className="col-lg-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3">
                    <img 
                      src="/images/advanced.png" 
                      alt="Advanced Level" 
                      className="img-fluid"
                      style={{ maxHeight: '150px' }}
                    />
                  </div>
                  <div className="plan-title text-center">
                    <h4 className="card-title text-success fw-bold">{t('level_cards.advanced.title')}</h4>
                  </div>
                  <div className="plan-description">
                    <p className="card-text text-muted">
                      {t('level_cards.advanced.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Native Level */}
            <div className="col-lg-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <div className="mb-3">
                    <img 
                      src="/images/native.png" 
                      alt="Native Level" 
                      className="img-fluid"
                      style={{ maxHeight: '150px' }}
                    />
                  </div>
                  <div className="plan-title text-center">
                    <h4 className="card-title text-warning fw-bold">{t('level_cards.native.title')}</h4>
                  </div>
                  <div className="plan-description">
                    <p className="card-text text-muted">
                      {t('level_cards.native.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center mt-3">
            <a
              href="https://portal.profezac.com/subscription/all5"
              className="btn btn-success btn-lg px-5 py-3 fw-bold"
              style={{
                fontSize: '1.2rem',
                borderRadius: '8px',
                boxShadow: '0 4px 15px rgba(40, 167, 69, 0.4)',
                textDecoration: 'none',
                minWidth: '280px'
              }}
            >
              {t('learning_system.cta_button')}
            </a>
          </div>
        </div>
      </section>

      {/* Testimonial 1 */}
      <section className="py-4" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="bg-white rounded shadow-sm p-4 border-start border-primary border-4">
                <div className="row">
                  <div className="col-9">
                    <p className="mb-2 fst-italic text-dark">
{t('testimonials.franco.text')}
                    </p>
                    <div>
                      <strong className="text-primary">{t('testimonials.franco.name')}</strong>
                      <span className="text-muted ms-1">{t('testimonials.franco.country')}</span>
                    </div>
                  </div>
                  <div className="col-3 d-flex justify-content-center align-items-center">
                    <div
                      className="rounded"
                      style={{
                        width: '100px',
                        height: '100px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <img 
                        src="/landing/testimonios/Franco.webp?t=20250811" 
                        alt="Franco V." 
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'top center'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Learning Struggles Section */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <div className="mb-4">
                <div className="d-flex justify-content-center align-items-center mb-3">
                  <div className="bg-white rounded-circle p-3 me-3 shadow-sm">
                    <span style={{ fontSize: '2rem' }}>💭</span>
                  </div>
                  <h3 className="mb-0 fw-bold text-dark">{t('pain_points.title')}</h3>
                </div>
                <p className="text-muted fs-6 mb-4">{t('pain_points.subtitle')}</p>
              </div>
              
              <div className="row g-3 mb-5">
                <div className="col-md-6">
                  <div className="bg-white rounded p-3 shadow-sm text-start">
                    <div className="d-flex align-items-start">
                      <div 
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                        style={{ width: '40px', height: '40px' }}
                      >
                        <span className="text-primary">•</span>
                      </div>
                      <p className="mb-0 fst-italic text-dark">
                        {t('pain_points.points.point1')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="bg-white rounded p-3 shadow-sm text-start">
                    <div className="d-flex align-items-start">
                      <div 
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                        style={{ width: '40px', height: '40px' }}
                      >
                        <span className="text-primary">•</span>
                      </div>
                      <p className="mb-0 fst-italic text-dark">
                        {t('pain_points.points.point2')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="bg-white rounded p-3 shadow-sm text-start">
                    <div className="d-flex align-items-start">
                      <div 
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                        style={{ width: '40px', height: '40px' }}
                      >
                        <span className="text-primary">•</span>
                      </div>
                      <p className="mb-0 fst-italic text-dark">
                        {t('pain_points.points.point3')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="bg-white rounded p-3 shadow-sm text-start">
                    <div className="d-flex align-items-start">
                      <div 
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                        style={{ width: '40px', height: '40px' }}
                      >
                        <span className="text-primary">•</span>
                      </div>
                      <p className="mb-0 fst-italic text-dark">
                        {t('pain_points.points.point4')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="bg-white rounded p-3 shadow-sm text-start">
                    <div className="d-flex align-items-start">
                      <div 
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                        style={{ width: '40px', height: '40px' }}
                      >
                        <span className="text-primary">•</span>
                      </div>
                      <p className="mb-0 fst-italic text-dark">
                        {t('pain_points.points.point5')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="bg-white rounded p-3 shadow-sm text-start">
                    <div className="d-flex align-items-start">
                      <div 
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                        style={{ width: '40px', height: '40px' }}
                      >
                        <span className="text-primary">•</span>
                      </div>
                      <p className="mb-0 fst-italic text-dark">
                        {t('pain_points.points.point6')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* First CTA Section */}
              <div className="bg-white rounded shadow-sm p-4">
                {/* Title takes full width */}
                <div className="row mb-3">
                  <div className="col-12 text-center">
                    <h4 className="fw-bold text-dark mb-0">
                      {t('cta_sections.first.title')}
                    </h4>
                  </div>
                </div>
                
                {/* Pricing with integrated image */}
                <div className="mb-4">
                  <SpecialPricingDisplay
                    pricingData={pricingData}
                    userLocation={userLocation}
                    loadingPricing={loadingPricing}
                    getOriginalPrice={getOriginalPrice}
                    getFormattedPrice={getFormattedPrice}
                    showImage={true}
                    imageSrc="/landing/images/ctaimage/pzleftthumbsup.png"
                    imageAlt="Profe Zac thumbs up"
                    imagePosition="right"
                    imageMaxWidth="200px"
                  />
                </div>
                <div className="row mt-4">
                  <div className="col-12 text-center">
                    <a
                      href="https://portal.profezac.com/subscription/all5"
                      className="btn btn-primary btn-lg w-100 px-4 py-3"
                      style={{ fontSize: '1.2rem', textDecoration: 'none' }}
                    >
                      {t('cta_sections.first.button')}
                    </a>
                    <div className="text-center mt-3 text-muted small" style={{ fontSize: '0.8rem' }}>
                      💳 Pago seguro • 🔒 Datos protegidos • ⚡ Activación inmediata
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Featured In Section */}
      <section className="py-5 text-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <h5 className="text-muted mb-4">{t('media.featured_text')}</h5>
          <img 
            src="/images/logo.png" 
            alt="As featured in major media outlets" 
            className="media-logos img-fluid"
            style={{ maxHeight: '60px', opacity: 0.7 }}
          />
        </div>
      </section>

      {/* Learning Methodology Section */}
      <section className="py-5" style={{ backgroundColor: 'white' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="text-center mb-5">
                <h2 className="display-5 fw-bold mb-4" style={{ color: '#333' }}>
{t('methodology.title')}
                </h2>
                <p className="lead text-muted">
{t('methodology.subtitle')}
                </p>
              </div>
              
              <div className="row g-4 mb-5">
                {/* Step 1: Ver */}
                <div className="col-md-6 col-lg-3">
                  <div className="card h-100 shadow-sm border-0">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px', flexShrink: 0 }}>
                          <span style={{ fontSize: '1.2rem' }}>👁️</span>
                        </div>
                        <div>
                          <h5 className="mb-1">{t('methodology.steps.see.title')}</h5>
                          <p className="text-muted small mb-0">{t('methodology.steps.see.description')}</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <div style={{ width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', borderRadius: '8px', boxShadow: 'rgba(0, 0, 0, 0.15) 0px 15px 30px' }}>
                          <video 
                            src="/images/0723noaudio.mp4" 
                            poster="/images/0723poster.png" 
                            autoPlay 
                            loop 
                            playsInline 
                            muted
                            onClick={(e) => {
                              if (e.target.paused) {
                                e.target.play();
                              } else {
                                e.target.pause();
                              }
                            }}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center', display: 'block', cursor: 'pointer' }}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Step 2: Ouvir */}
                <div className="col-md-6 col-lg-3">
                  <div className="card h-100 shadow-sm border-0">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px', flexShrink: 0 }}>
                          <span style={{ fontSize: '1.2rem' }}>👂</span>
                        </div>
                        <div>
                          <h5 className="mb-1">{t('methodology.steps.listen.title')}</h5>
                          <p className="text-muted small mb-0">{t('methodology.steps.listen.description')}</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <div 
                          className="rounded d-flex align-items-center justify-content-center" 
                          style={{ height: '120px', width: '100%', overflow: 'hidden', position: 'relative', aspectRatio: '16 / 9' }}
                        >
                          <audio controls className="w-100" style={{ borderRadius: '6px' }}>
                            <source src="/images/0723.MP3" type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Step 3: Asociar */}
                <div className="col-md-6 col-lg-3">
                  <div className="card h-100 shadow-sm border-0">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-warning text-white rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px', flexShrink: 0 }}>
                          <span style={{ fontSize: '1.2rem' }}>🧠</span>
                        </div>
                        <div>
                          <h5 className="mb-1">{t('methodology.steps.associate.title')}</h5>
                          <p className="text-muted small mb-0">{t('methodology.steps.associate.description')}</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <div style={{ width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', borderRadius: '8px', boxShadow: 'rgba(0, 0, 0, 0.15) 0px 15px 30px' }}>
                          <video 
                            src="/images/0723.mp4" 
                            poster="/images/0723poster.png" 
                            preload="metadata" 
                            playsInline 
                            webkit-playsinline="true"
                            controlsList="noremoteplayback nofullscreen nodownload"
                            disablePictureInPicture
                            x-webkit-airplay="deny"
                            onClick={(e) => {
                              if (e.target.paused) {
                                e.target.play();
                              } else {
                                e.target.pause();
                              }
                            }}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center', display: 'block', cursor: 'pointer' }}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Step 4: Confirmar */}
                <div className="col-md-6 col-lg-3">
                  <div className="card h-100 shadow-sm border-0">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-info text-white rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px', flexShrink: 0 }}>
                          <span style={{ fontSize: '1.2rem' }}>✅</span>
                        </div>
                        <div>
                          <h5 className="mb-1">{t('methodology.steps.confirm.title')}</h5>
                          <p className="text-muted small mb-0">{t('methodology.steps.confirm.description')}</p>
                        </div>
                      </div>
                      <div className="text-center">
                        <div style={{ width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', borderRadius: '8px', boxShadow: 'rgba(0, 0, 0, 0.15) 0px 15px 30px' }}>
                          <video 
                            src="/landing/images/0723subtitled.mp4?v=20250811" 
                            poster="/landing/images/confirmar.png" 
                            preload="metadata" 
                            playsInline 
                            webkit-playsinline="true"
                            controlsList="noremoteplayback nofullscreen nodownload"
                            disablePictureInPicture
                            x-webkit-airplay="deny"
                            onClick={(e) => {
                              if (e.target.paused) {
                                e.target.play();
                              } else {
                                e.target.pause();
                              }
                            }}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center', display: 'block', cursor: 'pointer' }}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* See How It Works - Complete Section */}
              <div className="row align-items-center mb-5">
                <div className="col-lg-6 mb-4 mb-lg-0">
                  <h3 className="mb-4">{t('demo_section.title')}</h3>
                  <p className="mb-4">
{t('demo_section.description')}
                  </p>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="bg-white rounded p-3 shadow-sm text-start">
                        <div className="d-flex align-items-start">
                          <div 
                            className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                            style={{ width: '40px', height: '40px' }}
                          >
                            <span style={{ fontSize: '1.2rem' }}>✅</span>
                          </div>
                          <p className="mb-0 fst-italic text-dark">
{t('demo_section.features.feature1')}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="bg-white rounded p-3 shadow-sm text-start">
                        <div className="d-flex align-items-start">
                          <div 
                            className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                            style={{ width: '40px', height: '40px' }}
                          >
                            <span style={{ fontSize: '1.2rem' }}>✅</span>
                          </div>
                          <p className="mb-0 fst-italic text-dark">
{t('demo_section.features.feature2')}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="bg-white rounded p-3 shadow-sm text-start">
                        <div className="d-flex align-items-start">
                          <div 
                            className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                            style={{ width: '40px', height: '40px' }}
                          >
                            <span style={{ fontSize: '1.2rem' }}>✅</span>
                          </div>
                          <p className="mb-0 fst-italic text-dark">
{t('demo_section.features.feature3')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div 
                      style={{ 
                        width: '100%', 
                        aspectRatio: '16 / 9', 
                        position: 'relative', 
                        overflow: 'hidden', 
                        borderRadius: '8px', 
                        boxShadow: 'rgba(0, 0, 0, 0.15) 0px 15px 30px' 
                      }}
                    >
                      <video 
                        src="/videos/0725.mp4" 
                        poster="/videos/0725poster.png" 
                        preload="metadata" 
                        playsInline
                        webkit-playsinline="true"
                        controlsList="noremoteplayback nofullscreen nodownload"
                        disablePictureInPicture
                        x-webkit-airplay="deny"
                        onClick={(e) => {
                          if (e.target.paused) {
                            e.target.play();
                          } else {
                            e.target.pause();
                          }
                        }}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover', 
                          objectPosition: 'center center', 
                          display: 'block',
                          cursor: 'pointer'
                        }}
                      >
                        Su navegador no soporta la etiqueta de video.
                      </video>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Results Statement */}
              <div className="text-center">
                <div className="bg-primary bg-opacity-10 rounded p-4">
                  <h4 className="fw-bold text-primary mb-3">
{t('results.title')}
                  </h4>
                  <p className="text-dark mb-3">
{t('results.description')}
                  </p>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="d-flex align-items-center justify-content-center">
                        <span className="text-success me-2" style={{ fontSize: '1.5rem' }}>✓</span>
                        <span className="fw-medium">{t('key_benefits.no_memorization')}</span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="d-flex align-items-center justify-content-center">
                        <span className="text-success me-2" style={{ fontSize: '1.5rem' }}>✓</span>
                        <span className="fw-medium">{t('key_benefits.measurable_progress')}</span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="d-flex align-items-center justify-content-center">
                        <span className="text-success me-2" style={{ fontSize: '1.5rem' }}>✓</span>
                        <span className="fw-medium">{t('key_benefits.real_confidence')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial 2 */}
      <section className="py-4" style={{ backgroundColor: 'white' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="bg-light rounded shadow-sm p-4 border-start border-success border-4">
                <div className="row">
                  <div className="col-9">
                    <p className="mb-2 fst-italic text-dark">
{t('testimonials.rodrigo.text')}
                    </p>
                    <div>
                      <strong className="text-success">{t('testimonials.rodrigo.name')}</strong>
                      <span className="text-muted ms-1">{t('testimonials.rodrigo.country')}</span>
                    </div>
                  </div>
                  <div className="col-3 d-flex justify-content-center align-items-center">
                    <div
                      className="rounded"
                      style={{
                        width: '100px',
                        height: '100px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <img 
                        src="/landing/testimonios/Rodrigo.webp" 
                        alt="Rodrigo M." 
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'top center'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Quiz Section */}
      <section className="py-4" style={{ backgroundColor: 'white' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="text-center mb-5">
                <h2 className="display-5 fw-bold mb-4" style={{ color: '#333' }}>
                  {t('quiz.title')}
                </h2>
                <p className="lead text-muted">
                  {t('quiz.description')}
                </p>
              </div>
              
              {/* Basic Level Quiz Component */}
              <BasicLevelQuiz />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial 3 */}
      <section className="py-4" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="bg-white rounded shadow-sm p-4 border-start border-warning border-4">
                <div className="row">
                  <div className="col-9">
                    <p className="mb-2 fst-italic text-dark">
{t('testimonials.laura.text')}
                    </p>
                    <div>
                      <strong className="text-warning">{t('testimonials.laura.name')}</strong>
                      <span className="text-muted ms-1">{t('testimonials.laura.country')}</span>
                    </div>
                  </div>
                  <div className="col-3 d-flex justify-content-center align-items-center">
                    <div
                      className="rounded"
                      style={{
                        width: '100px',
                        height: '100px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <img 
                        src="/landing/testimonios/Lauracolombia.webp" 
                        alt="Laura S." 
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'top center'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional CTA Section 1 */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="bg-white rounded shadow-sm p-4">
                <div className="row mb-3">
                  <div className="col-12 text-center">
                    <h3 className="fw-bold text-dark mb-2">
                      {t('final_cta.title')}
                    </h3>
                    <p className="text-muted mb-4">
                      {(() => {
                        let baseText = t('final_cta.description');
                        
                        // Apply country detection for dynamic text
                        if (userLocation && userLocation.countryCode) {
                          const countryData = getBasicGentilicio(userLocation.countryCode, currentLanguage);
                          baseText = baseText.replace(/latinos/gi, countryData.name);
                        }
                        
                        return baseText;
                      })()}
                    </p>
                  </div>
                </div>
                <SpecialPricingDisplay
                  pricingData={pricingData}
                  userLocation={userLocation}
                  loadingPricing={loadingPricing}
                  getOriginalPrice={getOriginalPrice}
                  getFormattedPrice={getFormattedPrice}
                  showImage={true}
                  imageSrc="/landing/images/zacarmscrossed.png"
                  imageAlt="Profe Zac confident"
                  imagePosition="left"
                  imageMaxWidth="250px"
                  showRealValue={false}
                  showCountryDiscount={true}
                  countryDiscountPrice="$15.00"
                />
                <div className="row mt-4">
                  <div className="col-12 text-center">
                    <a
                      href="https://portal.profezac.com/subscription/all5"
                      className="btn btn-success btn-lg w-100 px-4 py-3"
                      style={{ fontSize: '1.2rem', textDecoration: 'none' }}
                    >
                      {t('cta.start_transformation')}
                    </a>
                    <div className="text-center mt-3 text-muted small" style={{ fontSize: '0.8rem' }}>
                      💳 Pago seguro • 🔒 Datos protegidos • ⚡ Activación inmediata
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quiz Verification Section */}
      <section className="py-5" style={{ backgroundColor: 'white' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="bg-white rounded shadow-sm p-4">
                <QuizVerificationSection />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Daily Motivation Section */}
      <DailyMotivationSection />

      {/* David B. Testimonial */}
      <section className="py-4" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="bg-white rounded shadow-sm p-4 border-start border-primary border-4">
                <div className="row">
                  <div className="col-9">
                    <p className="mb-2 fst-italic text-dark">
                      {t('testimonials.david_b.text')}
                    </p>
                    <div>
                      <strong className="text-primary">{t('testimonials.david_b.name')}</strong>
                      <span className="text-muted ms-1">{t('testimonials.david_b.country')}</span>
                    </div>
                  </div>
                  <div className="col-3 d-flex justify-content-center align-items-center">
                    <div
                      className="rounded"
                      style={{
                        width: '100px',
                        height: '100px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <img 
                        src="/landing/testimonios/David.webp" 
                        alt="David B." 
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'top center'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional CTA Section 2 */}
      <section className="py-5" style={{ backgroundColor: 'white' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="bg-white rounded shadow-sm p-4 border border-primary border-2">
                <div className="row align-items-center mb-4">
                  <div className="col-md-8 text-md-start text-center">
                    <h3 className="fw-bold text-primary mb-3">
                      ¿Estás listo para dejar atrás la frustración de no entender inglés?
                    </h3>
                    <div className="mb-4">
                      <h5 className="fw-bold text-dark mb-2">Lo que obtienes INMEDIATAMENTE:</h5>
                      <ul className="list-unstyled">
                        <li className="mb-2"><span className="text-success fw-bold">✓</span> Acceso completo a los 3 niveles</li>
                        <li className="mb-2"><span className="text-success fw-bold">✓</span> Más de 500 videos educativos</li>
                        <li className="mb-2"><span className="text-success fw-bold">✓</span> Quizzes interactivos ilimitados</li>
                        <li className="mb-2"><span className="text-success fw-bold">✓</span> Seguimiento de progreso personal</li>
                        <li className="mb-2"><span className="text-success fw-bold">✓</span> Videos de motivación diaria de Zac</li>
                        <li className="mb-2"><span className="text-success fw-bold">✓</span> Soporte por email</li>
                      </ul>
                    </div>
                    <div className="mb-3">
                      <div className="text-primary small fw-medium mb-1" style={{ fontSize: '0.8rem' }}>
                        Precio especial solo por HOY:
                      </div>
                      <div className="fs-3 fw-bold text-primary mb-2">
                        Solo {loadingPricing ? '$5.00' : getFormattedPrice('basic', 'monthly', '$5.00')}
                      </div>
                      <div className="small text-muted">
                        <em>{t('final_cta.pricing.lock_price')}</em>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 text-center mt-3 mt-md-0">
                    <img 
                      src="/images/cutout Background Removed.png" 
                      alt="Profe Zac teaching" 
                      className="img-fluid rounded" 
                      style={{ maxWidth: '220px' }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 text-center">
                    <a
                      href="https://portal.profezac.com/subscription/all5"
                      className="btn btn-primary btn-lg w-100 px-4 py-3"
                      style={{ fontSize: '1.2rem', textDecoration: 'none' }}
                    >
                      Sí, Quiero Dominar el Inglés 🎯
                    </a>
                    <div className="small text-muted mt-2">
                      💳 Pago seguro • 🔒 Datos protegidos • ⚡ Activación inmediata
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Words Preview Section */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="text-center mb-5">
                <h2 className="fw-bold text-primary mb-3">Las 180 Palabras Esenciales que te Permiten Entender el 90% del Inglés Hablado</h2>
                <p className="text-muted">Vocabulario estratégicamente seleccionado para máxima comprensión con mínimo esfuerzo</p>
              </div>
              <div className="row justify-content-center">
                <div className="col-lg-8">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body p-4">
                      <div className="text-center mb-4">
                        <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                          <span className="fw-bold fs-4">180</span>
                        </div>
                        <h5>Vocabulario Esencial</h5>
                        <p className="text-muted mb-3">Cada palabra con su propio video explicativo y quiz interactivo</p>
                      </div>
                      <div className="mt-3">
                        <div className="mb-3">
                          <h6 className="text-primary mb-2">Ejemplos de palabras que aprenderás:</h6>
                          <div className="d-flex flex-column gap-2" id="word-list">
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">1. Happy</small>
                            </div>
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">2. Basketball</small>
                            </div>
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">3. Coffee</small>
                            </div>
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">4. How Are You</small>
                            </div>
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">5. House</small>
                            </div>
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">6. Play</small>
                            </div>
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">7. Want</small>
                            </div>
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">8. Beach</small>
                            </div>
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">9. Car</small>
                            </div>
                            <div className="bg-light rounded p-2 d-flex align-items-center">
                              <i className="fas fa-play-circle text-primary me-2"></i>
                              <small className="fw-medium">10. Listen</small>
                            </div>
                          </div>
                        </div>
                        <div className="bg-primary bg-opacity-10 rounded p-2 text-center">
                          <button 
                            className="btn btn-sm btn-link text-primary fw-bold p-0 text-decoration-none" 
                            style={{ fontSize: '0.875rem' }}
                            onClick={() => {
                              const wordList = document.getElementById('word-list');
                              const isExpanded = wordList.children.length > 10;
                              
                              if (!isExpanded) {
                                // Show all 180 words
                                const allWords = [
                                  "Brush Teeth", "Explore NYC", "Exploring the Farmhouse", "Getting Dressed", "Going to the Airport", "Going to the Gym", "Going to the Movies", "Making Popcorn", "Morning Routine", "Preparing Steak", "Ride the Bus", "Take Out the Trash", "The Date", "Time", "Angry", "Bad", "Close", "Cold", "Empty & Full", "Far", "Happy", "Heavy & Light", "Hot", "Old & New", "Open & Closed", "Quiet & Loud", "Right & Left", "Rough & Smooth", "Soft & Hard", "Strong & Weak", "Sweet & Sour", "Upside Down", "Wide & Narrow", "Here", "Slowly", "Have", "How Are You", "Want", "What", "When", "Where", "Who", "Art", "Backpack", "Basketball", "Bathroom", "Beach", "Bed", "Bedroom", "Belt", "Bike", "Box", "Bridge", "Buttons & Zippers", "Car", "Cards", "Cat", "Cell Phones", "Closet", "Coffee", "Couch", "Dishes", "Dog", "Farm", "Fire", "Fruits", "Garage", "Golf Cart", "Guitar", "Hotel Room", "House", "Kitchen", "Nuts", "Office", "Outerwear", "Park", "Patio", "Plants", "Playing Cards", "Puzzle", "Roads", "Salt & Pepper", "Sidewalk", "Skateboard", "Slide", "Snow", "Street", "Sunrise", "Table", "Tree", "Umbrella", "Video Game", "Across", "Around", "Behind", "Between", "By", "Down", "Far", "In", "In Front", "Inside", "Next To", "On Top", "Out", "Through", "Up", "Adjust", "Balance", "Bend", "Blow", "Break", "Brush", "Change", "Climb", "Close", "Cook", "Count", "Crush", "Cry & Laugh", "Cut", "Dig", "Drink", "Drop", "Dry", "Erase", "Fall", "Feel", "Find", "Fit", "Fly", "Go", "Have", "Hear", "Hit", "Hold", "Hug", "Jump", "Leave", "Like", "Listen", "Loosen", "Lose", "Make", "Measure", "Melt", "Pack", "Peel", "Plant", "Play", "Poke", "Pour", "Pull", "Push", "Reach", "Read", "Recycle", "Ride", "Roll", "Screw", "See", "Shop", "Sleep", "Slide", "Smell", "Squeeze", "Stick", "Straighten", "Swing", "Take", "Throw", "To Be", "To Buy", "Touch", "Try On", "Use", "Wash", "Watch", "Wet"
                                ];
                                
                                wordList.innerHTML = allWords.map((word, index) => `
                                  <div class="bg-light rounded p-2 d-flex align-items-center">
                                    <i class="fas fa-play-circle text-primary me-2"></i>
                                    <small class="fw-medium">${index + 1}. ${word}</small>
                                  </div>
                                `).join('');
                                
                                this.textContent = '↑ Ver menos';
                              } else {
                                // Show only first 10
                                const firstTen = [
                                  "Happy", "Basketball", "Coffee", "How Are You", "House", "Play", "Want", "Beach", "Car", "Listen"
                                ];
                                
                                wordList.innerHTML = firstTen.map((word, index) => `
                                  <div class="bg-light rounded p-2 d-flex align-items-center">
                                    <i class="fas fa-play-circle text-primary me-2"></i>
                                    <small class="fw-medium">${index + 1}. ${word}</small>
                                  </div>
                                `).join('');
                                
                                this.textContent = '+ Ver las 180 palabras';
                              }
                            }}
                          >
                            + Ver las 180 palabras
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits of Learning These Words */}
              <div className="row mt-5">
                <div className="col-12">
                  <div className="row g-4">
                    <div className="col-md-6 col-lg-3">
                      <div className="text-center p-4 bg-white rounded shadow-sm h-100">
                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                          <i className="fas fa-brain text-primary fs-4"></i>
                        </div>
                        <h5 className="fw-bold text-dark mb-2">Comprensión Instantánea</h5>
                        <p className="small text-muted mb-0">Estas 180 palabras aparecen en el 90% de conversaciones diarias en inglés</p>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-3">
                      <div className="text-center p-4 bg-white rounded shadow-sm h-100">
                        <div className="bg-success bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                          <i className="fas fa-rocket text-success fs-4"></i>
                        </div>
                        <h5 className="fw-bold text-dark mb-2">Aprendizaje Acelerado</h5>
                        <p className="small text-muted mb-0">Construye fluidez rápidamente con el vocabulario más utilizado</p>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-3">
                      <div className="text-center p-4 bg-white rounded shadow-sm h-100">
                        <div className="bg-warning bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                          <i className="fas fa-target text-warning fs-4"></i>
                        </div>
                        <h5 className="fw-bold text-dark mb-2">Enfoque Estratégico</h5>
                        <p className="small text-muted mb-0">Evita memorizar miles de palabras innecesarias</p>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-3">
                      <div className="text-center p-4 bg-white rounded shadow-sm h-100">
                        <div className="bg-info bg-opacity-10 rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                          <i className="fas fa-comments text-info fs-4"></i>
                        </div>
                        <h5 className="fw-bold text-dark mb-2">Confianza Real</h5>
                        <p className="small text-muted mb-0">Domina el vocabulario que realmente necesitas para conversar</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Andressa Brazil Testimonial Section */}
      <section className="py-4 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="bg-white rounded shadow-sm p-4 border-start border-primary border-4">
                <div className="row">
                  <div className="col-9">
                    <p className="mb-2 fst-italic text-dark">Siempre tuve miedo de hablar inglés. Con este curso, ahora me siento segura para conversar con nativos.</p>
                    <div>
                      <strong className="text-primary">Andressa L.</strong>
                      <span className="text-muted ms-1">🇧🇷</span>
                    </div>
                  </div>
                  <div className="col-3 d-flex justify-content-center align-items-center">
                    <div 
                      className="rounded" 
                      style={{
                        width: '100px',
                        height: '100px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <img 
                        src="/landing/testimonios/Andressabrazil.webp" 
                        alt="Andressa L." 
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'top center'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section with Special Pricing */}
      <section className="py-5" style={{ backgroundColor: 'white' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="bg-white rounded shadow-sm p-4">
                <div className="row mb-3">
                  <div className="col-12 text-center">
                    <h4 className="fw-bold text-dark mb-0">
                      ¡Es hora de que comiences a entender inglés! Actúa ahora para asegurar este precio
                    </h4>
                  </div>
                </div>
                <div className="mb-4">
                  <SpecialPricingDisplay
                    pricingData={pricingData}
                    userLocation={userLocation}
                    loadingPricing={loadingPricing}
                    getOriginalPrice={(plan, period, multiplier, fallback) => 
                      loadingPricing ? fallback : `${getCurrencySymbol()}${(pricingData?.[plan]?.[period]?.amount * multiplier || fallback.replace(/\$/, '')).toLocaleString()}`
                    }
                    getFormattedPrice={getFormattedPrice}
                    showImage={true}
                    imageSrc="/landing/images/ctaimage/pzrightthumbsup.png"
                    imageAlt="Profe Zac thumbs up"
                    imagePosition="right"
                    imageMaxWidth="200px"
                    showRealValue={true}
                    showCountryDiscount={true}
                    realValuePrice="$20.00"
                    countryDiscountPrice="$10.00"
                  />
                </div>
                <div className="row mt-4">
                  <div className="col-12 text-center">
                    <a 
                      href="https://portal.profezac.com/subscription/all5" 
                      className="btn btn-primary btn-lg w-100 px-4 py-3" 
                      style={{ 
                        fontSize: '1.2rem', 
                        textDecoration: 'none' 
                      }}
                    >
                      Quiero aprender inglés desde cero 👉
                    </a>
                    <div className="text-center mt-3 text-muted small" style={{ fontSize: '0.8rem' }}>
                      💳 Pago seguro • 🔒 Datos protegidos • ⚡ Activación inmediata
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Email Guide Modal */}
      <EmailGuideModal 
        show={showEmailGuideModal} 
        onHide={() => setShowEmailGuideModal(false)} 
      />
    </div>
  );
};

export default ProfezacLanding;